import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { UserPlus, UserCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UserFollowButtonProps {
  targetUserId: string;
  targetUserType: 'supabase' | 'internal';
  currentUserId: string;
  currentUserType: 'supabase' | 'internal';
  targetUserName?: string;
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
}

export const UserFollowButton: React.FC<UserFollowButtonProps> = ({
  targetUserId,
  targetUserType,
  currentUserId,
  currentUserType,
  targetUserName = 'user',
  size = 'sm',
  variant = 'outline'
}) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkFollowStatus();
  }, [targetUserId, currentUserId]);

  const checkFollowStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', currentUserId)
        .eq('follower_type', currentUserType)
        .eq('followed_id', targetUserId)
        .eq('followed_type', targetUserType)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setIsFollowing(!!data);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const handleFollowToggle = async () => {
    if (currentUserId === targetUserId) {
      toast({
        title: "Error",
        description: "You cannot follow yourself",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      if (isFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('follower_type', currentUserType)
          .eq('followed_id', targetUserId)
          .eq('followed_type', targetUserType);

        if (error) throw error;

        setIsFollowing(false);
        toast({
          title: "Unfollowed",
          description: `You are no longer following ${targetUserName}`,
        });
      } else {
        // Follow
        const { error } = await supabase
          .from('user_follows')
          .insert({
            follower_id: currentUserId,
            follower_type: currentUserType,
            followed_id: targetUserId,
            followed_type: targetUserType
          });

        if (error) throw error;

        setIsFollowing(true);
        toast({
          title: "Following",
          description: `You are now following ${targetUserName}`,
        });

        // Track follow activity (fire-and-forget)
        import('@/services/activityTrackingService').then(({ activityTrackingService }) => {
          activityTrackingService.recordActivity({
            userId: currentUserId,
            userType: currentUserType,
            activityType: 'followed_user',
            activityTitle: `Started following ${targetUserName}`,
            activityData: { followedUserId: targetUserId, followedUserType: targetUserType },
            isPublic: true
          });
        }).catch(console.error);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast({
        title: "Error",
        description: "Failed to update follow status",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Don't show button for self
  if (currentUserId === targetUserId) {
    return null;
  }

  return (
    <Button
      variant={isFollowing ? 'default' : variant}
      size={size}
      onClick={handleFollowToggle}
      disabled={loading}
      className={isFollowing ? 'bg-green-600 hover:bg-green-700' : ''}
    >
      {isFollowing ? (
        <>
          <UserCheck className="h-4 w-4 mr-2" />
          Following
        </>
      ) : (
        <>
          <UserPlus className="h-4 w-4 mr-2" />
          Follow
        </>
      )}
    </Button>
  );
};